import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Import Leaflet CSS in your index.html or here
// <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />

const GeolocationPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tracking');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Location tracking state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [trackingInterval, setTrackingInterval] = useState(null);

  // Geofencing state
  const [geofences, setGeofences] = useState([]);
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [geofenceEvents, setGeofenceEvents] = useState([]);

  // Floor plans state
  const [floorPlans, setFloorPlans] = useState([]);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);

  // Map refs
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Initialize map when component mounts
    initializeMap();
    
    // Load initial data
    loadGeofences();
    loadFloorPlans();
    
    // Cleanup on unmount
    return () => {
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }
    };
  }, []);

  const initializeMap = async () => {
    try {
      // Dynamically import Leaflet to avoid SSR issues
      const L = await import('leaflet');
      
      // Fix for default markers in React
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      });

      if (mapRef.current && !leafletMapRef.current) {
        // Initialize map with default view
        leafletMapRef.current = L.map(mapRef.current).setView([51.505, -0.09], 13);
        
        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(leafletMapRef.current);

        // Get user's current location
        getCurrentLocation();
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map. Please refresh the page.');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          altitude: position.coords.altitude
        };
        
        setCurrentLocation(location);
        setIsLoading(false);
        
        // Center map on current location
        if (leafletMapRef.current) {
          leafletMapRef.current.setView([location.latitude, location.longitude], 15);
          
          // Add marker for current location
          const L = window.L;
          if (L) {
            L.marker([location.latitude, location.longitude])
              .addTo(leafletMapRef.current)
              .bindPopup('Your current location')
              .openPopup();
          }
        }
      },
      (error) => {
        setIsLoading(false);
        setError(`Location access denied: ${error.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  const startTracking = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsTracking(true);
    setError('');
    
    // Track location every 10 seconds
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData = {
            entity_id: user.id,
            entity_type: 'user',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            altitude: position.coords.altitude
          };

          try {
            // Send location to backend
            const response = await fetch(`${BACKEND_URL}/api/geolocation/locations`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(locationData)
            });

            if (response.ok) {
              const savedLocation = await response.json();
              setLocationHistory(prev => [savedLocation, ...prev.slice(0, 99)]); // Keep last 100
              setCurrentLocation(locationData);
              
              // Update map marker
              updateMapMarker(locationData);
            } else {
              console.error('Failed to save location:', response.statusText);
            }
          } catch (error) {
            console.error('Error saving location:', error);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }, 10000); // Every 10 seconds

    setTrackingInterval(interval);
  };

  const stopTracking = () => {
    setIsTracking(false);
    if (trackingInterval) {
      clearInterval(trackingInterval);
      setTrackingInterval(null);
    }
  };

  const updateMapMarker = (location) => {
    if (leafletMapRef.current && window.L) {
      const L = window.L;
      
      // Remove existing markers
      leafletMapRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          leafletMapRef.current.removeLayer(layer);
        }
      });
      
      // Add new marker
      L.marker([location.latitude, location.longitude])
        .addTo(leafletMapRef.current)
        .bindPopup(`Current location<br>Accuracy: ${location.accuracy?.toFixed(1)}m`)
        .openPopup();
      
      // Center map on new location
      leafletMapRef.current.setView([location.latitude, location.longitude]);
    }
  };

  const loadGeofences = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/geolocation/geofences`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeofences(data);
      }
    } catch (error) {
      console.error('Error loading geofences:', error);
    }
  };

  const loadFloorPlans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/geolocation/floor-plans`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFloorPlans(data);
      }
    } catch (error) {
      console.error('Error loading floor plans:', error);
    }
  };

  const loadLocationHistory = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/geolocation/locations/${user.id}?limit=50`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocationHistory(data);
      }
    } catch (error) {
      console.error('Error loading location history:', error);
    }
  };

  const renderTrackingTab = () => (
    <div className="space-y-6">
      {/* Current Location Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Real-Time Location Tracking
        </h3>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <button
            onClick={getCurrentLocation}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Getting Location...' : 'Get Current Location'}
          </button>
          
          <button
            onClick={isTracking ? stopTracking : startTracking}
            className={`px-4 py-2 rounded-md text-white ${
              isTracking 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isTracking ? 'Stop Tracking' : 'Start Tracking'}
          </button>
          
          <button
            onClick={loadLocationHistory}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Load History
          </button>
        </div>

        {currentLocation && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 mb-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Current Location</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
              <p>Latitude: {currentLocation.latitude?.toFixed(6)}</p>
              <p>Longitude: {currentLocation.longitude?.toFixed(6)}</p>
              <p>Accuracy: {currentLocation.accuracy?.toFixed(1)}m</p>
              <p>Speed: {currentLocation.speed ? `${currentLocation.speed.toFixed(1)} m/s` : 'N/A'}</p>
            </div>
          </div>
        )}

        {/* Map Container */}
        <div className="bg-gray-200 rounded-md overflow-hidden" style={{ height: '400px' }}>
          <div ref={mapRef} style={{ height: '100%', width: '100%' }}></div>
        </div>

        {isTracking && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700 text-sm">
              🟢 Location tracking is active. Your location is being updated every 10 seconds.
            </p>
          </div>
        )}
      </div>

      {/* Location History */}
      {locationHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Location History
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Coordinates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Accuracy
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {locationHistory.slice(0, 10).map((location, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {new Date(location.timestamp || location.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {location.accuracy?.toFixed(1)}m
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderGeofencingTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Advanced Geofencing
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Create and manage geofences to monitor when people or assets enter or exit specific areas.
        </p>
        
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Geofencing Coming Soon
          </h4>
          <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
            Advanced geofencing features including zone creation, breach alerts, and event monitoring will be available soon.
          </p>
        </div>
      </div>
    </div>
  );

  const renderFloorPlansTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Interactive Floor Plans
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Upload and manage floor plans for indoor asset tracking and zone management.
        </p>
        
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Floor Plans Coming Soon
          </h4>
          <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
            Interactive floor plan features including upload, zone creation, and indoor tracking will be available soon.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Geolocation & Floor Plans
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Track locations, manage geofences, and monitor indoor spaces
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Mobile Tab Dropdown */}
          <div className="sm:hidden mb-4">
            <label htmlFor="tab-select" className="sr-only">Select a tab</label>
            <select
              id="tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="tracking">📍 Real-Time GPS Tracking</option>
              <option value="geofencing">🚧 Advanced Geofencing</option>
              <option value="floorplans">🏢 Interactive Floor Plans</option>
            </select>
          </div>

          {/* Desktop Tab Navigation */}
          <nav className="hidden sm:flex -mb-px overflow-x-auto scrollbar-hide">
            <div className="flex space-x-2 lg:space-x-8 min-w-max px-1">
              <button
                onClick={() => setActiveTab('tracking')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'tracking'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">📍 Real-Time GPS Tracking</span>
                <span className="lg:hidden">📍 GPS</span>
              </button>
              <button
                onClick={() => setActiveTab('geofencing')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'geofencing'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">🚧 Advanced Geofencing</span>
                <span className="lg:hidden">🚧 Geofencing</span>
              </button>
              <button
                onClick={() => setActiveTab('floorplans')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'floorplans'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">🏢 Interactive Floor Plans</span>
                <span className="lg:hidden">🏢 Floor Plans</span>
              </button>
            </div>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'tracking' && renderTrackingTab()}
          {activeTab === 'geofencing' && renderGeofencingTab()}
          {activeTab === 'floorplans' && renderFloorPlansTab()}
        </div>
      </div>
    </div>
  );
};

export default GeolocationPage;